import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import { z } from 'zod';

import { Box, Button, Heading, Paragraph, Text } from '@rover/kibble/core';
import { Form, useKibbleForm } from '@rover/kibble/forms';
import TextField from '@rover/kibble/forms/fields/TextField';
import Banner from '@rover/kibble/patterns/Banner';
import LocationInputContainer from '@rover/react-lib/src/containers/LocationInputContainer';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import { apiLocationGeocodeRetrieve } from '@rover/rsdk/src/apiClient/latest/api-location-geocode-retrieve';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { SearchNameCardClickedEvent } from '../analyticsEvents';
import SearchByNameSitterCard from '../components/SearchByNameSitterCard';
import { MAX_NAME_LENGTH, MIN_NAME_LENGTH } from '../constants';
import type { SearchParams } from '../hooks/useSearchByName';
import type { NameSearchResult } from '../types';

const NAME_FIELD = 'sitterName';
const LOCATION_INPUT_ID = 'search-by-name-location';

type Props = {
  initialLocation?: string;
  results: NameSearchResult[] | null;
  query: string;
  isLoading: boolean;
  error: string | null;
  searchUuid: string | null;

  onSearch: (params: SearchParams) => void;
};

const ModalSearchScreen = ({
  initialLocation = '',
  results,
  query,
  isLoading,
  error,
  searchUuid,
  onSearch,
}: Props): JSX.Element => {
  const headingId = useId();
  const { setScreenReaderAnnouncement } = useA11yContext();
  const [location, setLocation] = useState(initialLocation);
  const [lat, setLat] = useState<string | null>(null);
  const [lng, setLng] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  // Track current location for the seed-effect race guard without re-running the effect.
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);
  const nameInputRef = useRef<HTMLElement | null>(null);

  // Geocode resolves a free-text location string to coords. Returns null on
  // failure so the caller can surface a validation error.
  const geocodeLocation = useCallback(
    async (locationQuery: string): Promise<{ lat: string; lng: string } | null> => {
      try {
        const data = await apiLocationGeocodeRetrieve({ query: locationQuery });
        if (data?.lat != null && data?.lng != null) {
          return { lat: data.lat, lng: data.lng };
        }
        return null;
      } catch {
        return null;
      }
    },
    []
  );

  const { configureField, formProps, watch } = useKibbleForm({
    schema: z.object({
      sitterName: z.string().trim().min(MIN_NAME_LENGTH, 'name-too-short'),
    }),
    defaultValues: { sitterName: '' },
    onValidSubmit: async (data) => {
      if (isLoading || isResolvingLocation) return;

      const trimmedLocation = location.trim();
      if (!trimmedLocation) {
        const message = t`Please enter a location`;
        setLocationError(message);
        setScreenReaderAnnouncement(message);
        // Kibble's LocationInputContainer doesn't forward a ref; focus by id.
        // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
        document.getElementById(LOCATION_INPUT_ID)?.focus();
        return;
      }

      // fall back to a submit-time geocode rather than sending the request without location
      let resolvedLat = lat;
      let resolvedLng = lng;
      if (resolvedLat == null || resolvedLng == null) {
        setIsResolvingLocation(true);
        const coords = await geocodeLocation(trimmedLocation);
        setIsResolvingLocation(false);
        if (!coords) {
          const message = t`We couldn't find that location. Try a city, state, or zip code.`;
          setLocationError(message);
          setScreenReaderAnnouncement(message);
          // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
          document.getElementById(LOCATION_INPUT_ID)?.focus();
          return;
        }
        resolvedLat = coords.lat;
        resolvedLng = coords.lng;
        // Cache the resolved coords so a repeat submit with the same string
        // doesn't re-geocode.
        setLat(resolvedLat);
        setLng(resolvedLng);
      }

      // Clamp before passing through to the API + headings/SR announcements,
      // so a pathological query can't be interpolated verbatim into UI text.
      const clampedName = data.sitterName.slice(0, MAX_NAME_LENGTH);
      onSearch({
        name: clampedName,
        lat: resolvedLat,
        lng: resolvedLng,
      });
    },
  });

  const nameFieldConfig = configureField(NAME_FIELD);
  const nameFieldErrorMessage = nameFieldConfig.fieldError
    ? t`Please enter at least ${MIN_NAME_LENGTH} characters of the sitter's name`
    : undefined;

  const handleLocationChange = useCallback(
    (
      data: {
        value?: string | null;
        lat?: string | null;
        lng?: string | null;
      } | null
    ) => {
      const val = data?.value ?? '';
      setLocation(val);
      setLat(data?.lat ?? null);
      setLng(data?.lng ?? null);
      if (val.trim()) setLocationError(null);
    },
    []
  );

  // Pre-warm coords for the seeded location string. We only apply the response
  // when the location field still matches the seed; if the user has typed a
  // different value (or cleared it), the seed coords would mis-pair with the
  // current string, so we discard them.
  useEffect(() => {
    const seed = initialLocation.trim();
    if (!seed) return undefined;

    let cancelled = false;
    apiLocationGeocodeRetrieve({ query: seed })
      .then((data) => {
        if (cancelled) return undefined;
        if (locationRef.current.trim() !== seed) return undefined;
        if (data?.lat != null) setLat(data.lat);
        if (data?.lng != null) setLng(data.lng);
        return undefined;
      })
      .catch(() => {
        // Geocode failure here is non-fatal — the submit handler will retry
        // synchronously and surface a user-visible error if it also fails.
      });

    return (): void => {
      cancelled = true;
    };
  }, [initialLocation]);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  // Focus the results heading on new results so SR + keyboard users land in the result region.
  useEffect(() => {
    if (results === null || isLoading || error) return;
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    const heading = document.getElementById(headingId);
    heading?.focus();
  }, [results, isLoading, error, headingId]);

  const hasSearched = results !== null;
  const resultCount = results?.length ?? 0;
  const hasResults = resultCount > 0;
  const isSubmitting = isLoading || isResolvingLocation;
  // Disable submit until both fields are filled with valid content. The zod
  // schema still gates onValidSubmit, but greying out the button gives users
  // an earlier signal than a post-click error.
  const nameValue = watch(NAME_FIELD) ?? '';
  const isFormIncomplete =
    nameValue.trim().length < MIN_NAME_LENGTH || location.trim().length === 0;

  const handleCardClick = useCallback(
    (result: NameSearchResult, index: number): void => {
      if (!searchUuid) return;
      emitAnalyticsEvent(
        new SearchNameCardClickedEvent({
          rank: index,
          sitterPersonOpk: result.personOpk,
          resultCount,
          searchUuid,
          isPreviouslyBooked: result.isPreviouslyBooked,
        })
      );
    },
    [searchUuid, resultCount]
  );

  return (
    <Box testID="search-by-name-screen">
      <Box p="4x">
        <Form {...formProps} hideRequiredFieldWarning>
          <Box mb="4x">
            <TextField
              {...nameFieldConfig}
              translatedFieldErrorMessage={nameFieldErrorMessage}
              primaryLabel={t`Sitter name`}
              placeholder={t`Enter sitter's name`}
              hideRequiredAsterisk
              webInputRef={(el): void => {
                nameInputRef.current = el;
              }}
            />
          </Box>
          <Box mb="6x">
            <Text
              as="label"
              fontWeight="semibold"
              mb="2x"
              display="block"
              webProps={{ htmlFor: LOCATION_INPUT_ID }}
            >
              {t`Location`}
            </Text>
            <LocationInputContainer
              inputId={LOCATION_INPUT_ID}
              inputValue={location}
              onChange={handleLocationChange}
              placeholder={t`City, state, or zip code`}
              validationType="inline"
              validationError={locationError}
              channel="search-by-name"
              enableLatLng
            />
          </Box>
          <Button
            variant="primary"
            fullWidth
            webProps={{ type: 'submit' }}
            loading={isSubmitting}
            disabled={isSubmitting || isFormIncomplete}
            testID="search-by-name-submit"
          >
            {t`Search`}
          </Button>
        </Form>
      </Box>

      {error && (
        <Box px="4x" pb="2x">
          <Banner severity="danger" testID="search-by-name-error">
            {error}
          </Banner>
        </Box>
      )}

      {hasSearched && !error && (
        <>
          <Box px="4x" pb="2x">
            <Banner severity="info" testID="search-by-name-privacy-banner">
              {t`For sitter safety, location details are hidden in these results.`}
            </Banner>
          </Box>

          {/* Heading's webProps doesn't accept tabIndex; hang the focus target on the wrapping Box.
              aria-labelledby still resolves to the heading text via the Box's accessible name. */}
          <Box px="4x" pb="2x" webProps={{ id: headingId, tabIndex: -1 }}>
            <Heading size="300" testID="search-by-name-results-heading">
              {query ? t`Results for ${query}` : t`Results`}
            </Heading>
          </Box>

          <Box px="4x">
            {!isLoading &&
              (!hasResults ? (
                <Box
                  py="8x"
                  textAlign="center"
                  testID="search-by-name-empty-results"
                  webAs="section"
                  webProps={{ 'aria-labelledby': headingId }}
                >
                  <Paragraph size="200" textColor="secondary">
                    {t`No sitters found matching ${query}.`}
                  </Paragraph>
                  <Text size="100" textColor="tertiary" mt="2x" mb="4x" display="block">
                    {t`Try a different spelling or location.`}
                  </Text>
                </Box>
              ) : (
                <Box
                  webAs="ul"
                  webProps={{ 'aria-labelledby': headingId }}
                  p="0x"
                  m="0x"
                  sx={{ listStyle: 'none' }}
                >
                  {results?.map((result, index) => (
                    <Box webAs="li" key={result.personOpk}>
                      <SearchByNameSitterCard
                        result={result}
                        onCardClick={() => handleCardClick(result, index)}
                      />
                    </Box>
                  ))}
                </Box>
              ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ModalSearchScreen;
